import React, { useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Card, CardBody, FormControl, LayoutContainer, MessageBlock } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { isPasswordChangeConfirmed, PASSWORD_RESET_REQUIRED_STORAGE_KEY } from '../utils/authRequest';
import { validateRequiredPasswordChange } from '../utils/passwordPolicy';
import '../styles/login.css';

const PASSWORD_REQUIREMENTS_TEXT = 'Entre 8 y 32 caracteres, con al menos una letra y un número.';
const PASSWORD_VALID_TEXT = 'Cumple los requisitos de seguridad';
const PASSWORD_MISMATCH_TEXT = 'Las contraseñas no coinciden';
const PASSWORD_MATCH_TEXT = 'Las contraseñas coinciden';

function newPasswordPolicyMessage(password) {
  return validateRequiredPasswordChange({
    currentPassword: 'present',
    password,
    passwordConfirmation: password,
  }).password || '';
}

function firstFieldError(errors, field) {
  const value = errors?.[field];
  if (Array.isArray(value)) {
    return value[0] || '';
  }
  if (typeof value === 'string') {
    return value;
  }
  return '';
}

function positiveStatusMessage(text) {
  return (
    <>
      <i className="bi bi-check" aria-hidden="true" />{text}
    </>
  );
}

function passwordToggleButton(shown, onToggle, label, disabled) {
  const action = shown ? 'Ocultar' : 'Mostrar';

  return (
    <button
      type="button"
      className="rg-login__password-toggle"
      onClick={onToggle}
      aria-label={`${action} ${label}`}
      title={`${action} ${label}`}
      disabled={disabled}
    >
      <i className={`bi ${shown ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden="true"></i>
    </button>
  );
}

const RequiredPasswordChangePage = () => {
  const { authToken, passwordResetRequired, sessionEpoch, completePasswordChange, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [completedPath, setCompletedPath] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const submitLock = useRef(false);
  const logoutLock = useRef(false);
  const pageEpoch = useRef(0);

  const storedToken = localStorage.getItem('authToken');
  const sessionToken = authToken || (storedToken && storedToken !== 'undefined' ? storedToken : '');
  const mustChangePassword = passwordResetRequired
    || localStorage.getItem(PASSWORD_RESET_REQUIRED_STORAGE_KEY) === '1';

  if (completedPath) {
    return <Navigate to={completedPath} replace />;
  }

  if (!isLoggingOut && !sessionToken) {
    return <Navigate to="/login" replace />;
  }

  if (!isLoggingOut && !mustChangePassword) {
    return <Navigate to="/" replace />;
  }

  const sessionStillCurrent = (epoch) => !logoutLock.current && epoch === pageEpoch.current;

  const handleLogout = async () => {
    if (logoutLock.current) {
      return;
    }

    logoutLock.current = true;
    pageEpoch.current += 1;
    setIsLoggingOut(true);

    try {
      await logout();
    } finally {
      setCompletedPath('/login');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitLock.current || logoutLock.current) {
      return;
    }

    const requestEpoch = sessionEpoch;
    const epoch = pageEpoch.current;
    setSubmitAttempted(true);

    const clientErrors = validateRequiredPasswordChange({
      currentPassword,
      password,
      passwordConfirmation,
    });
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors({
        current_password: clientErrors.current_password || '',
      });
      setFormError('');
      return;
    }

    submitLock.current = true;
    setIsSubmitting(true);
    setFieldErrors({});
    setFormError('');

    try {
      const response = await api.post('/password', {
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation,
      });

      if (!sessionStillCurrent(epoch)) {
        return;
      }

      if (!isPasswordChangeConfirmed(response.data)) {
        setFormError('El cambio de contraseña no quedó confirmado.');
        return;
      }

      const nextToken = response.data?.access_token || sessionToken;
      completePasswordChange(nextToken, {
        passwordResetRequired: false,
        sessionEpoch: requestEpoch,
      });
      setCompletedPath('/');
    } catch (error) {
      if (!sessionStillCurrent(epoch)) {
        return;
      }

      const status = error.response?.status;
      const errors = error.response?.data?.errors;
      if (status === 422 && errors) {
        setFieldErrors({
          current_password: firstFieldError(errors, 'current_password'),
          password: firstFieldError(errors, 'password'),
          password_confirmation: firstFieldError(errors, 'password_confirmation'),
        });
        return;
      }

      setFormError(error.response?.data?.message || 'No se pudo actualizar la contraseña.');
    } finally {
      submitLock.current = false;
      if (!logoutLock.current) {
        setIsSubmitting(false);
      }
    }
  };

  const formBusy = isSubmitting || isLoggingOut;
  const clientPasswordMessage = password
    ? newPasswordPolicyMessage(password)
    : (submitAttempted ? newPasswordPolicyMessage('') : '');
  const passwordMessage = fieldErrors.password || clientPasswordMessage;
  const passwordIsValid = Boolean(password) && !newPasswordPolicyMessage(password) && !fieldErrors.password;
  const confirmationDiffers = passwordConfirmation !== password;
  const confirmationServerMessage = fieldErrors.password_confirmation || '';
  const showConfirmationMismatch = !confirmationServerMessage && (
    (passwordConfirmation !== '' && confirmationDiffers)
    || (submitAttempted && password !== '' && confirmationDiffers)
  );
  const showConfirmationMatch = !confirmationServerMessage
    && !showConfirmationMismatch
    && passwordConfirmation !== ''
    && passwordIsValid;
  const confirmationMessage = confirmationServerMessage
    || (showConfirmationMismatch ? PASSWORD_MISMATCH_TEXT : '')
    || (showConfirmationMatch ? positiveStatusMessage(PASSWORD_MATCH_TEXT) : '');
  const confirmationStatus = confirmationServerMessage || showConfirmationMismatch
    ? 'error'
    : (showConfirmationMatch ? 'valid' : 'default');

  const clearFieldError = (field) => {
    setFieldErrors((current) => (
      current[field] ? { ...current, [field]: '' } : current
    ));
  };

  return (
    <div id="auth-left" className="rg-login rg-login--required-password">
      <LayoutContainer size="md" className="rg-login__container">
        <Card className="rg-login__card">
          <CardBody className="rg-login__card-body">
            <h1 className="auth-title rg-login__title">Definir contraseña</h1>

            <div className="rg-spacing-stack rg-login__alerts">
              <MessageBlock tone="warning" title="Contraseña temporal">
                Para continuar, definí una contraseña nueva.
              </MessageBlock>
              {formError && (
                <MessageBlock tone="error" title="No se pudo guardar">
                  {formError}
                </MessageBlock>
              )}
            </div>

            <form onSubmit={handleSubmit} className="rg-login__form rg-spacing-stack" noValidate aria-busy={formBusy}>
              <FormControl
                id="current-password"
                label="Contraseña actual"
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(event) => {
                  setCurrentPassword(event.target.value);
                  clearFieldError('current_password');
                }}
                autoComplete="current-password"
                disabled={formBusy}
                status={fieldErrors.current_password ? 'error' : 'default'}
                message={fieldErrors.current_password || undefined}
                iconRightInteractive
                iconRight={passwordToggleButton(showCurrentPassword, () => setShowCurrentPassword((current) => !current), 'contraseña actual', formBusy)}
              />

              <FormControl
                id="new-password"
                label="Nueva contraseña"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  clearFieldError('password');
                }}
                autoComplete="new-password"
                disabled={formBusy}
                status={passwordMessage ? 'error' : (passwordIsValid ? 'valid' : 'default')}
                message={passwordMessage || (passwordIsValid ? positiveStatusMessage(PASSWORD_VALID_TEXT) : undefined)}
                hint={passwordMessage || passwordIsValid ? undefined : PASSWORD_REQUIREMENTS_TEXT}
                iconRightInteractive
                iconRight={passwordToggleButton(showPassword, () => setShowPassword((current) => !current), 'nueva contraseña', formBusy)}
              />

              <FormControl
                id="password-confirmation"
                label="Confirmar contraseña"
                type={showPasswordConfirmation ? 'text' : 'password'}
                value={passwordConfirmation}
                onChange={(event) => {
                  setPasswordConfirmation(event.target.value);
                  clearFieldError('password_confirmation');
                }}
                autoComplete="new-password"
                disabled={formBusy}
                status={confirmationStatus}
                message={confirmationMessage || undefined}
                iconRightInteractive
                iconRight={passwordToggleButton(showPasswordConfirmation, () => setShowPasswordConfirmation((current) => !current), 'confirmar contraseña', formBusy)}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={formBusy}
                aria-busy={isSubmitting || undefined}
              >
                {isSubmitting ? 'Guardando...' : 'Guardar contraseña'}
              </Button>
              <div className="rg-login__logout">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isLoggingOut}
                  aria-busy={isLoggingOut || undefined}
                  onClick={handleLogout}
                >
                  {isLoggingOut ? 'Cerrando sesión...' : 'Cerrar sesión'}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </LayoutContainer>
    </div>
  );
};

export default RequiredPasswordChangePage;
